import React, { useEffect, useRef, useState } from 'react'
import { KTSVG } from '../../../../../_metronic/helpers'
import { GenerationAnalysisType } from '../../../../services/generationAnalysisService'
import styles from '../../generationAnalysis.module.css'

export type PeriodRange = { start: string; end: string }

type Props = {
  type: GenerationAnalysisType
  value: PeriodRange
  onChange: (value: PeriodRange) => void
  maxItems: number
}

export const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

const pad = (value: number) => value.toString().padStart(2, '0')

const now = new Date()
const CURRENT_YEAR = now.getFullYear()
const CURRENT_MONTH_KEY = `${CURRENT_YEAR}-${pad(now.getMonth() + 1)}`

/** "YYYY-MM" -> índice absoluto de mês, para comparar e medir intervalos. */
const monthIndex = (key: string) => {
  const [year, month] = key.split('-').map(Number)
  return year * 12 + (month - 1)
}

const shiftMonth = (key: string, delta: number) => {
  const index = monthIndex(key) + delta
  return `${Math.floor(index / 12)}-${pad((index % 12) + 1)}`
}

export const formatMonthKey = (key: string) => {
  const [year, month] = key.split('-').map(Number)
  return `${MONTH_NAMES[month - 1]}/${year}`
}

export const countPeriodItems = (type: GenerationAnalysisType, range: PeriodRange) =>
  type === 'annual'
    ? Number(range.end) - Number(range.start) + 1
    : monthIndex(range.end) - monthIndex(range.start) + 1

export const defaultPeriod = (type: GenerationAnalysisType): PeriodRange =>
  type === 'annual'
    ? { start: String(CURRENT_YEAR), end: String(CURRENT_YEAR) }
    : { start: shiftMonth(CURRENT_MONTH_KEY, -2), end: CURRENT_MONTH_KEY }

export const formatPeriod = (type: GenerationAnalysisType, range: PeriodRange) => {
  if (type === 'annual') {
    return range.start === range.end ? range.start : `${range.start} – ${range.end}`
  }
  return range.start === range.end
    ? formatMonthKey(range.start)
    : `${formatMonthKey(range.start)} – ${formatMonthKey(range.end)}`
}

export function PeriodRangePicker({ type, value, onChange, maxItems }: Props) {
  const [open, setOpen] = useState(false)
  // Primeiro clique define o início; segundo clique fecha o intervalo
  const [anchor, setAnchor] = useState<string | null>(null)
  const [hovered, setHovered] = useState<string | null>(null)
  const [viewYear, setViewYear] = useState<number>(CURRENT_YEAR)
  const containerRef = useRef<HTMLDivElement>(null)

  const isAnnual = type === 'annual'
  // Anos exibidos em blocos de 12
  const yearPageStart = viewYear - (((viewYear - 2000) % 12) + 12) % 12

  useEffect(() => {
    if (!open) return
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
        setAnchor(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  useEffect(() => {
    setViewYear(Number(value.end.substring(0, 4)) || CURRENT_YEAR)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type])

  const toIndex = (key: string) => (isAnnual ? Number(key) : monthIndex(key))

  const cells = isAnnual
    ? Array.from({ length: 12 }, (_, index) => String(yearPageStart + index))
    : MONTH_NAMES.map((_, index) => `${viewYear}-${pad(index + 1)}`)

  const isFuture = (key: string) => (isAnnual ? Number(key) > CURRENT_YEAR : monthIndex(key) > monthIndex(CURRENT_MONTH_KEY))

  const exceedsLimit = (key: string) => anchor !== null && Math.abs(toIndex(key) - toIndex(anchor)) + 1 > maxItems

  // Intervalo exibido: prévia durante a seleção ou o valor confirmado
  const previewStart = anchor ? (hovered && toIndex(hovered) < toIndex(anchor) ? hovered : anchor) : value.start
  const previewEnd = anchor ? (hovered && toIndex(hovered) > toIndex(anchor) ? hovered : anchor) : value.end

  const handleSelect = (key: string) => {
    if (isFuture(key) || exceedsLimit(key)) return
    if (!anchor) {
      setAnchor(key)
      return
    }
    const [start, end] = toIndex(key) < toIndex(anchor) ? [key, anchor] : [anchor, key]
    onChange({ start, end })
    setAnchor(null)
    setHovered(null)
    setOpen(false)
  }

  const presets: { label: string; range: PeriodRange }[] = isAnnual
    ? [
        { label: 'Este ano', range: { start: String(CURRENT_YEAR), end: String(CURRENT_YEAR) } },
        { label: 'Últimos 2 anos', range: { start: String(CURRENT_YEAR - 1), end: String(CURRENT_YEAR) } },
        { label: 'Últimos 3 anos', range: { start: String(CURRENT_YEAR - 2), end: String(CURRENT_YEAR) } },
      ]
    : [
        { label: 'Este mês', range: { start: CURRENT_MONTH_KEY, end: CURRENT_MONTH_KEY } },
        { label: 'Últimos 3 meses', range: { start: shiftMonth(CURRENT_MONTH_KEY, -2), end: CURRENT_MONTH_KEY } },
        { label: 'Últimos 6 meses', range: { start: shiftMonth(CURRENT_MONTH_KEY, -5), end: CURRENT_MONTH_KEY } },
        { label: 'Este ano', range: { start: `${CURRENT_YEAR}-01`, end: CURRENT_MONTH_KEY } },
      ]

  return (
    <div className={styles.periodPicker} ref={containerRef}>
      <button
        type='button'
        className={`form-control form-control-solid d-flex align-items-center justify-content-between ${styles.periodButton}`}
        onClick={() => setOpen(!open)}
      >
        <span className='d-flex align-items-center gap-2 text-truncate'>
          <KTSVG path='/media/icons/duotune/files/fil002.svg' className='svg-icon-2 svg-icon-primary' />
          <span className='fw-semibold text-gray-800 text-truncate'>{formatPeriod(type, value)}</span>
        </span>
        <span className='badge badge-light-primary ms-2'>
          {countPeriodItems(type, value)} {isAnnual ? (countPeriodItems(type, value) > 1 ? 'anos' : 'ano') : (countPeriodItems(type, value) > 1 ? 'meses' : 'mês')}
        </span>
      </button>

      {open && (
        <div className={`card shadow-sm ${styles.periodPopover}`}>
          <div className='card-body p-4'>
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <button type='button' className='btn btn-sm btn-icon btn-light' onClick={() => setViewYear(viewYear - (isAnnual ? 12 : 1))}>
                <i className='bi bi-chevron-left' />
              </button>
              <span className='fw-bold fs-5 text-gray-800'>
                {isAnnual ? `${yearPageStart} – ${yearPageStart + 11}` : viewYear}
              </span>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light'
                disabled={isAnnual ? yearPageStart + 11 >= CURRENT_YEAR : viewYear >= CURRENT_YEAR}
                onClick={() => setViewYear(viewYear + (isAnnual ? 12 : 1))}
              >
                <i className='bi bi-chevron-right' />
              </button>
            </div>

            <div className={styles.periodGrid} onMouseLeave={() => setHovered(null)}>
              {cells.map((key) => {
                const index = toIndex(key)
                const inRange = index >= toIndex(previewStart) && index <= toIndex(previewEnd)
                const isEdge = key === previewStart || key === previewEnd
                const disabled = isFuture(key) || exceedsLimit(key)
                return (
                  <button
                    key={key}
                    type='button'
                    disabled={disabled}
                    onMouseEnter={() => setHovered(key)}
                    onClick={() => handleSelect(key)}
                    className={`${styles.periodCell} ${inRange ? styles.periodCellInRange : ''} ${isEdge ? styles.periodCellEdge : ''}`}
                  >
                    {isAnnual ? key : MONTH_NAMES[Number(key.split('-')[1]) - 1]}
                  </button>
                )
              })}
            </div>

            <div className='text-muted fs-7 mt-3'>
              <i className='bi bi-info-circle me-1' />
              {anchor
                ? `Agora clique no ${isAnnual ? 'ano' : 'mês'} final (máx. ${maxItems} ${isAnnual ? 'anos' : 'meses'}).`
                : `Clique no ${isAnnual ? 'ano' : 'mês'} inicial e depois no final. Para um só, clique duas vezes.`}
            </div>

            <div className='separator my-3' />

            <div className='d-flex flex-wrap gap-2'>
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type='button'
                  className='btn btn-sm btn-light-primary py-2 px-3'
                  onClick={() => {
                    onChange(preset.range)
                    setAnchor(null)
                    setOpen(false)
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
